import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import type { GroupType, Option } from "./types";
import { flattenItems } from "./utils";

export type ComboboxEmptyProps = {
  children: React.ReactNode;
  addNewOption?: (filterValue: string, onAdd: () => void) => React.ReactNode;
  filteredItems: Option[] | GroupType[];
  filterValue: string;
  onAddItem?: (value: string) => void;
  disableAddingOptions?: boolean;
  containerPadding?: number;
};

export function ComboboxEmpty({
  children,
  addNewOption,
  filteredItems,
  filterValue,
  onAddItem,
  disableAddingOptions,
  containerPadding,
}: ComboboxEmptyProps) {
  const flatItems = flattenItems(filteredItems);

  if (flatItems.length > 0) return null;

  const handleAdd = () => {
    if (onAddItem && filterValue) {
      onAddItem(filterValue);
    }
  };

  const addingButton = disableAddingOptions
    ? null
    : onAddItem &&
      filterValue &&
      (addNewOption ? (
        addNewOption(filterValue, handleAdd)
      ) : (
        <DefaultAddButton onClick={handleAdd}>Add "{filterValue}"</DefaultAddButton>
      ));

  return (
    <EmptyContainer containerPadding={containerPadding}>
      {typeof children === "string" ? <Text color="sysTextSecondary">{children}</Text> : children}
      {addingButton}
    </EmptyContainer>
  );
}

const EmptyContainer = styled(Flex)<{ containerPadding?: number }>(
  ({ theme, containerPadding = 16 }) => ({
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    color: theme.colors.sysTextSecondary,
    padding: containerPadding,
    width: "100%",
  }),
);

const DefaultAddButton = styled.button(({ theme }) => ({
  padding: "8px 16px",
  fontSize: 14,
  fontWeight: 500,
  color: theme.colors.sysPrimaryDefault,
  backgroundColor: "transparent",
  border: `1px solid ${theme.colors.sysPrimaryDefault}`,
  borderRadius: 6,
  cursor: "pointer",
  transition: "all 0.15s ease",
  fontFamily: "inherit",

  "&:hover": {
    backgroundColor: theme.colors.sysPrimarySubtle,
  },

  "&:active": {
    backgroundColor: theme.colors.sysPrimarySubtleAlt,
  },
}));
